<template>
  <v-dialog
    :model-value="isVisible"
    width="600"
    persistent
    @click:outside="closeDialog"
    data-testid="create-loadtype-trigger-dialog">
    <v-card>
      <v-form>
        <v-card-title class="d-flex flex-row ma-2 mb-0">
          <h4 class="typography-heading-md">
            {{ t('customForms.headers.createForm') }}
          </h4>
          <v-spacer></v-spacer>
          <v-btn icon="mdi-close" variant="text" density="comfortable" @click="closeDialog"></v-btn>
        </v-card-title>
        <v-card-text>
          <div class="d-flex flex-column ma-2">
            <p class="mb-8">
              {{
                t(
                  'customForms.messages.startByNamingYourFormAndSelectingTheLoadTypesForWhichItWillBeRequired'
                )
              }}
            </p>

            <div class="d-flex flex-column gap-4">
              <div>
                <label>
                  <strong>
                    {{ t('customForms.labels.formName') }}
                    <span class="text-red">*</span>
                  </strong>
                </label>
                <v-text-field
                  v-model="customFormNameRef"
                  class="mt-3"
                  required
                  :placeholder="t('customForms.placeholders.eGRefrigeratedCargoOnly')"
                  variant="outlined"
                  data-testid="create-loadtype-trigger-name-input"></v-text-field>
              </div>

              <div>
                <label>
                  <strong>
                    {{ t('global.specialtyTerms.loadTypes', 2) }}
                    <span class="text-red">*</span>
                  </strong>
                </label>
                <load-type-select
                  :selected-warehouse="warehouse"
                  :placeholder="t('customForms.placeholders.selectAtLeastOne')"
                  data-testid="create-loadtype-trigger-loadtype-select"
                  variant="outlined"
                  outlined
                  :visible-count="3"
                  :return-object="false"
                  v-model="loadTypesRef"
                  :clearable="false"
                  hide-icon
                  :show-org-load-types="false"
                  required
                  multi-select />
              </div>
            </div>
          </div>
        </v-card-text>
        <v-divider />
        <v-card-actions class="ma-2">
          <v-spacer></v-spacer>
          <v-btn
            variant="outlined"
            @click="closeDialog"
            data-testid="create-loadtype-trigger-cancel-button">
            {{ t('global.buttons.nevermind') }}
          </v-btn>
          <v-btn
            data-testid="create-loadtype-trigger-submit-button"
            :disabled="!isFormValid"
            class="bg-primary"
            @click="submit">
            {{ t('customForms.buttons.createForm') }}
          </v-btn>
        </v-card-actions>
      </v-form>
    </v-card>
  </v-dialog>
</template>

<script>
import { computed, defineComponent, ref } from 'vue';
import { useNotify } from '@/composables';
import warehouseService from '@satellite/services/warehouse-service';
import { useI18n } from 'vue-i18n';

export default defineComponent({
  name: 'CustomFormsCreateLoadTypeTriggerDialog',
  emits: ['close', 'form-created'],
  props: {
    isVisible: {
      type: Boolean,
      default: false
    },
    warehouse: {
      type: Object,
      required: true
    }
  },
  setup(props, { emit }) {
    const notify = useNotify();
    const { t } = useI18n();

    const customFormNameRef = ref('');
    const loadTypesRef = ref([]);
    const isFormValid = computed(
      () => loadTypesRef.value.length > 0 && customFormNameRef.value.trim().length > 0
    );

    const closeDialog = () => {
      customFormNameRef.value = '';
      loadTypesRef.value = [];
      emit('close');
    };

    const submit = () => {
      if (!isFormValid.value) {
        return;
      }
      return warehouseService
        .createLoadTypesCustomForms(props.warehouse.id, loadTypesRef.value, customFormNameRef.value)
        .then(res => {
          if (res?.ok) {
            notify(t('customForms.messages.loadTypeFormCreatedSuccessfully'), 'success');
            emit('form-created', res.trigger);
          } else {
            throw new Error(t('customForms.messages.failedToCreateLoadTypeForm'));
          }
        })
        .catch(err => {
          console.error(err);
          notify(t('customForms.messages.failedToCreateLoadTypeForm'), 'error');
        })
        .finally(() => {
          closeDialog();
        });
    };

    return {
      loadTypesRef,
      customFormNameRef,
      closeDialog,
      isFormValid,
      submit,
      t
    };
  }
});
</script>
